/* src/lessons/cards/LessonCard/index.js */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { isEmpty, map, toUpper } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Checkbox,
  Divider,
  FormControl, FormControlLabel,
  Input, InputAdornment, InputLabel,
  Modal,
  Paper,
  Typography,
} from '@material-ui/core';
import { School, AccessTime, LocationOn } from '@material-ui/icons';

import { ConfirmationPopup, TextAdornment } from '../../../../app/components';
import styles from './styles';

import { lessonSetInfo } from './actions';
import { refundLesson, fetchLesson } from '../../actions';
import { getLessonInfo } from '../../selectors';
import { loadTexts } from '../../../../config/texts';

const TEXT_KEY = 'lessons';

class LessonCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      amount: 0,
      refundAll: false,
      openConfirmation: false,
      texts: loadTexts(TEXT_KEY, props.locale)
    };
    this._onChangeAmount = this._onChangeAmount.bind(this);
    this._onRefundAll = this._onRefundAll.bind(this);
    this._onRefundStudent = this._onRefundStudent.bind(this);

    this._onOpenConfirmation = this._onOpenConfirmation.bind(this);
    this._onCloseConfirmation = this._onCloseConfirmation.bind(this);
  }

  _onChangeAmount(event) {
    this.setState({ amount: event.target.value });
  }

  _onRefundAll(event) {
    const { target: { checked } } = event;
    const state = { refundAll: checked };
    if (checked) {
      const { info: { charges, price } } = this.props;
      const [{ refunded }] = charges;
      state.amount = Math.max(price - refunded, 0);
    }
    this.setState(state);
  }

  async _onRefundStudent() {
    const { amount } = this.state;
    const { info: { _id, studentID } } = this.props;
    await this.props.refundLesson({
      lesson: _id,
      student: studentID,
      amount,
    });
    // refetch lesson here
    await this.props.fetchLesson({ lesson: _id });
    this.props.lessonSetInfo(this.props.updatedInfo);
    this.props.onRefundStudent();
    this.setState({ refundAll: false, amount: 0, openConfirmation: false });
  }

  _onOpenConfirmation() {
    this.setState({ openConfirmation: true });
  }

  _onCloseConfirmation() {
    this.setState({ openConfirmation: false });
  }

  renderNames(classes, info) {
    const { texts } = this.state;
    return (
      <div className={classes.row}>
        <div className={classes.item}>
          <Typography>{texts.fields.student}</Typography>
          <Typography>{info.student}</Typography>
        </div>
        <div className={classes.item}>
          <Typography>{texts.fields.tutor}</Typography>
          <Typography>{info.tutor}</Typography>
        </div>
      </div>
    );
  }

  renderStatuses(classes, info) {
    const { texts } = this.state;
    const canceled = info.status === 'CANCELED';
    return (
      <div className={classes.row}>
        <div className={classes.item}>
          <Typography>{texts.fields.lesson_status}</Typography>
          <Typography color={canceled ? 'secondary' : 'default'}>{info.status}</Typography>
        </div>
        {canceled && (
          <div className={classes.item}>
            <Typography>{texts.fields.cancelled}</Typography>
            <Typography>{toUpper(info.canceled)}</Typography>
          </div>
        )}
        <div className={classes.item}>
          <Typography>{texts.fields.last_updated}</Typography>
          <Typography>{info.lastUpdated}</Typography>
        </div>
        {/* <div className={classes.item}>
          <Typography>Payment Status</Typography>
          <Typography>Initialized</Typography>
        </div> */}
      </div>
    );
  }

  renderButtons(classes, refunded) {
    const { amount, refundAll, texts: { button_text } } = this.state;
    const { info: { price } } = this.props;

    return (
      <div className={classes.buttons}>
        <div />
        <div className={classes.charge}>
          <FormControlLabel
            control={
              <Checkbox
                checked={refundAll}
                onChange={this._onRefundAll}
                value="refundAll"
                color="primary"
              />
            }
            label={button_text.refund_all}
          />
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel htmlFor="adornment-amount">{`${button_text.amount} (ex: 1000 => 10.00)`}</InputLabel>
            <Input
              id="adornment-amount"
              type="number"
              value={amount}
              disabled={refundAll}
              onChange={this._onChangeAmount}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
            />
          </FormControl>
          <Button
            variant="raised"
            disabled={amount <= 0 || amount > price - refunded}
            onClick={this._onOpenConfirmation}
          >
            {button_text.refund}
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const { classes, open, onClose, info } = this.props;
    const { openConfirmation, texts } = this.state;
    let refunded = 0;
    if (!isEmpty(info.charges) && info.charges[0].refunded) ([{ refunded }] = info.charges);

    return (
      <Modal
        aria-labelledby={texts.title.lesson_card}
        aria-describedby="Description of one lesson"
        open={open}
        onClose={onClose}
      >
        <Paper className={classes.paper}>
          {this.renderNames(classes, info)}
          <Divider />
          <div className={classes.row}>
            <div>
              <TextAdornment Adornment={School} text={info.course} />
              <TextAdornment Adornment={AccessTime} text={info.date} subtext={info.time} />
              <TextAdornment Adornment={LocationOn} text={info.location} subtext={info.address} />
            </div>
            <div>
              <Typography>{`${texts.fields.price}: ${(info.price / 100).toFixed(2)}`}</Typography>
              <Typography>{`${texts.fields.student_fee}: ${info.studentFee}`}</Typography>
              <Typography>{`${texts.fields.tutor_fee}: ${info.tutorFee}`}</Typography>
              {map(info.charges, ({ creditsUsed, refunded: r }) => (
                <React.Fragment>
                  <Typography>
                    {`${texts.fields.credits_used}: ${(creditsUsed / 100).toFixed(2)}`}
                  </Typography>
                  {refunded > 0 && (
                    <Typography color="secondary">
                      {`${texts.fields.refunded}: ${(r / 100).toFixed(2)}`}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <Divider />
          <div>
            {this.renderStatuses(classes, info)}
            {this.renderButtons(classes, refunded)}
          </div>
          <ConfirmationPopup
            open={openConfirmation}
            title={texts.pop_up.title}
            subtitle={texts.pop_up.subtitle}
            affirmativeButtonText={texts.pop_up.button_text}
            onClose={this._onCloseConfirmation}
            onSubmit={this._onRefundStudent}
          />
        </Paper>
      </Modal>
    );
  }
}

LessonCard.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  info: PropTypes.object.isRequired,
  refundLesson: PropTypes.func.isRequired,
  onRefundStudent: PropTypes.func,
  fetchLesson: PropTypes.func.isRequired,
  lessonSetInfo: PropTypes.func.isRequired,
  updatedInfo: PropTypes.object.isRequired,
};

LessonCard.defaultProps = {
  onRefundStudent: () => {},
};

const mapStateToProps = state => ({
  info: state.lessons.card.info,
  updatedInfo: getLessonInfo(state),
  locale: state.appState.locale
});

const mapDispatchToProps = dispatch => ({
  refundLesson: params => dispatch(refundLesson(params)()),
  fetchLesson: params => dispatch(fetchLesson(params)()),
  lessonSetInfo: params => dispatch(lessonSetInfo(params)),
});

export default compose(
  withStyles(styles, { name: 'LessonCard' }),
  connect(mapStateToProps, mapDispatchToProps),
)(LessonCard);



// WEBPACK FOOTER //
// ./src/modules/lessons/cards/LessonCard/index.js